/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/uploadStats.scss';

import {AwesomeIcon, ChangeFunction, Loader} from '@dvrd/dvr-controls';

import {IconName, IconPrefix} from '@fortawesome/fontawesome-svg-core';

import classNames from 'classnames';

import React from 'react';

import {useTranslation} from 'react-i18next';

import {AccessKey, InvoiceUploadStats} from '../../../util/interfaces';

import BolmateSwitch from '../../controls/switch/bolmateSwitch';

import InfoIcon from '../../icon/infoIcon';

import {parseThousandSeparator} from '../../../util/utils';

import useRight from '../../misc/useRight';

interface Props {
    onChangeProactiveUpload: ChangeFunction<boolean>;
    onChangeAutoRespond: ChangeFunction<boolean>;
    proactiveEnabled: boolean;
    autoRespondEnabled: boolean;
    stats: InvoiceUploadStats;
    loading: boolean;
}

export default function UploadStats(props: Props) {
    const {t} = useTranslation();
    const [canEdit] = useRight(AccessKey.INVOICE_REQUEST_SETTINGS);
    const {onChangeProactiveUpload, proactiveEnabled, stats, loading, onChangeAutoRespond, autoRespondEnabled} = props;

    function renderStat(
        icon: IconName,
        value: number,
        totalValue: number | string,
        label: string,
        className: string,
        iconPrefix: IconPrefix = 'fas'
    ) {
        return (
            <div className='stat dashboard-stat'>
                <Loader active={loading} size='2.5rem' />
                <div className={classNames('icon-container', className)}>
                    <AwesomeIcon name={icon} className='stat-icon' prefix={iconPrefix} />
                </div>
                <label className='stat-label'>{label}</label>
                <label className='stat-value'>{totalValue === -1 ? '-' : parseThousandSeparator(totalValue)}</label>
                {value > -1 && (
                    <label className='relative-value'>
                        <span className='green'>+{parseThousandSeparator(value)}</span>{' '}
                        {t('dashboard.stats.last30Days', 'AFGELOPEN 30 DAGEN')}
                    </label>
                )}
            </div>
        );
    }

    function renderSettings() {
        return (
            <div className='stat dashboard-stat'>
                <Loader active={loading} size='2.5rem' />
                <div className={classNames('icon-container', 'invoices')}>
                    <AwesomeIcon name='wrench' className='stat-icon' />
                </div>
                <div className='toggle'>
                    <label className='toggle-label'>
                        {t('dashboard.uploadStats.proactiveUpload', 'Proactief uploaden')}
                    </label>
                    <BolmateSwitch
                        value={proactiveEnabled}
                        onChange={onChangeProactiveUpload}
                        disabled={!canEdit}
                        activeGreen
                    />
                </div>
                <div className='toggle'>
                    <label className='toggle-label'>
                        {t('dashboard.uploadStats.autoRespond', 'Auto aanvragen beantwoorden')}
                    </label>
                    <BolmateSwitch
                        value={autoRespondEnabled}
                        onChange={onChangeAutoRespond}
                        disabled={!canEdit}
                        activeGreen
                    />
                </div>
                <div className='info-container'>
                    <InfoIcon className='info-icon' />
                    <p className='info-message'>
                        <b>{t('dashboard.uploadStats.proactiveUpload', 'Proactief uploaden')}</b>
                        <br />
                        {t(
                            'dashboard.uploadStats.proactiveInfo',
                            'Met proactief uploaden zorg je ervoor dat voor elke nieuwe order, de factuur na voltooiing van order wordt geüpload. Klanten kunnen de btw-factuur dan vinden en downloaden in hun Bol klantportaal. Zo voorkom je factuuraanvragen.'
                        )}
                        <br />
                        <br />
                        <b>{t('dashboard.uploadStats.autoRespondTitle', 'Automatisch aanvragen beantwoorden')}</b>
                        <br />
                        {t(
                            'dashboard.uploadStats.autoRespondInfo',
                            'Indien de factuur van een order nog niet is geüpload, kan een klant de factuur aanvragen. Met deze optie wordt er automatisch gereageerd op deze aanvraag met de bijbehorende factuur. Door ook de optie proactief uploaden aan te vinken, zorg je ervoor dat er steeds minder van dit soort aanvragen zijn, omdat de factuur al klaar zal staan en men deze niet meer aanvraagt.'
                        )}
                        <br />
                        <br />
                        <b>{t('dashboard.uploadStats.recommended', 'Aanbevolen')}</b>
                        <br />
                        {t(
                            'dashboard.uploadStats.recommendedInfo',
                            'Wij raden aan zowel proactief uploaden als automatisch beantwoorden aan te vinken.'
                        )}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className='stats dashboard-stats upload-stats'>
            {renderStat(
                'file-invoice',
                -1,
                stats.open,
                t('dashboard.uploadStats.openRequests', 'OPEN FACTUURAANVRAGEN'),
                'invoices'
            )}
            {renderStat(
                'cloud-arrow-up',
                stats.closed.thirty_days,
                stats.closed.total,
                t('dashboard.uploadStats.uploaded', 'FACTUREN GEÜPLOAD'),
                'sent'
            )}
            {renderSettings()}
        </div>
    );
}
